import { useEffect, useState } from 'react';

type Role = 'Owner' | 'Manager' | 'Moderator' | 'Analyst' | 'Developer';

type Member = {
  id: string;
  name: string;
  email: string;
  role: Role;
  active: boolean;
};

const SEED: readonly Member[] = [
  { id: 'u1', name: 'Elena Wortmann', email: 'elena@tomi-reviews.com', role: 'Owner', active: true },
  { id: 'u2', name: 'Marco Albert', email: 'marco@wortmann-retail.com', role: 'Manager', active: true },
  { id: 'u3', name: 'Sofia Conti', email: 'sofia.conti@tamaris.com', role: 'Moderator', active: true },
  { id: 'u4', name: 'Lukas Brunner', email: 'lukas@tomi-reviews.com', role: 'Analyst', active: true },
  { id: 'u5', name: 'Hadi Yousef', email: 'hadi@tomi-reviews.com', role: 'Developer', active: false },
];

const ROLES: readonly Role[] = ['Owner', 'Manager', 'Moderator', 'Analyst', 'Developer'];

const PERMISSIONS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'reviews_read', label: 'Reviews lesen' },
  { key: 'reviews_moderate', label: 'Reviews moderieren' },
  { key: 'rules_edit', label: 'Moderationsregeln bearbeiten' },
  { key: 'products_import', label: 'Produkte importieren' },
  { key: 'reports_send', label: 'Auto-Reports konfigurieren' },
  { key: 'integrations_edit', label: 'Integrationen verwalten' },
  { key: 'users_invite', label: 'User einladen' },
  { key: 'billing', label: 'Billing verwalten' },
];

const ROLE_PERMS: Record<Role, ReadonlyArray<'yes' | 'partial' | 'no'>> = {
  Owner:     ['yes', 'yes', 'yes', 'yes', 'yes', 'yes', 'yes', 'yes'],
  Manager:   ['yes', 'yes', 'yes', 'yes', 'yes', 'partial', 'yes', 'no'],
  Moderator: ['yes', 'yes', 'no', 'no', 'no', 'no', 'no', 'no'],
  Analyst:   ['yes', 'no', 'no', 'no', 'partial', 'no', 'no', 'no'],
  Developer: ['yes', 'no', 'partial', 'partial', 'no', 'yes', 'no', 'no'],
};

export function TeamPage() {
  const [members, setMembers] = useState<Member[]>([...SEED]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);

  const open = openId ? members.find((m) => m.id === openId) ?? null : null;

  function update(id: string, patch: Partial<Member>) {
    setMembers((ms) => ms.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h2 className="page-title">Team</h2>
          <p className="page-sub">User-Verwaltung mit Rollen und Permissions.</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn primary" onClick={() => setShowInvite(true)}>
            <span className="material-symbols-rounded">person_add</span>
            User einladen
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 0, marginBottom: 16 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>User</th>
              <th>E-Mail</th>
              <th>Rolle</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} style={{ cursor: 'pointer' }} onClick={() => setOpenId(m.id)}>
                <td>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <span className="avatar">{m.name.split(' ').map((p) => p[0]).join('').slice(0, 2)}</span>
                    <span style={{ font: '600 13px/18px var(--font-body)' }}>{m.name}</span>
                  </div>
                </td>
                <td className="mono" style={{ color: 'var(--fg-2)' }}>{m.email}</td>
                <td>
                  <span className="chip">{m.role}</span>
                </td>
                <td>
                  <span className={`badge ${m.active ? 'published' : 'hidden'}`}>
                    {m.active ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </td>
                <td>
                  <span className="material-symbols-rounded" style={{ color: 'var(--fg-3)', fontSize: 18 }}>
                    chevron_right
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <div className="card-title">Rollen-Matrix</div>
            <div className="card-sub">Welche Rolle darf was — verbindlich für alle Tenants</div>
          </div>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Permission</th>
              {ROLES.map((r) => (
                <th key={r} style={{ textAlign: 'center', width: 90 }}>{r}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PERMISSIONS.map((perm, pi) => (
              <tr key={perm.key}>
                <td style={{ font: '500 13px/18px var(--font-body)' }}>{perm.label}</td>
                {ROLES.map((role) => {
                  const v = ROLE_PERMS[role][pi];
                  return (
                    <td key={role} style={{ textAlign: 'center' }}>
                      <PermCell value={v} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <MemberDrawer
          member={open}
          onClose={() => setOpenId(null)}
          onChange={(patch) => update(open.id, patch)}
        />
      )}

      {showInvite && <InviteDrawer onClose={() => setShowInvite(false)} />}
    </div>
  );
}

function PermCell({ value }: { value: 'yes' | 'partial' | 'no' | undefined }) {
  if (value === 'yes') return <span style={{ color: 'var(--brand-green)', fontWeight: 700 }}>✓</span>;
  if (value === 'partial') return <span style={{ color: 'var(--brand-orange)', fontWeight: 700 }}>~</span>;
  return <span style={{ color: 'var(--fg-4)' }}>—</span>;
}

function MemberDrawer({
  member,
  onClose,
  onChange,
}: {
  member: Member;
  onClose: () => void;
  onChange: (patch: Partial<Member>) => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="scrim" onClick={onClose} aria-hidden="true" />
      <aside className="drawer" role="dialog" aria-label={`User ${member.name}`}>
        <header className="drawer-head">
          <h2>{member.name}</h2>
          <span className={`badge ${member.active ? 'published' : 'hidden'}`}>
            {member.active ? 'Aktiv' : 'Inaktiv'}
          </span>
          <button type="button" className="iconbtn" onClick={onClose} aria-label="Schließen">
            <span className="material-symbols-rounded">close</span>
          </button>
        </header>
        <div className="drawer-body">
          <div className="d-section">
            <div className="d-label">E-Mail</div>
            <div className="mono" style={{ font: '500 14px/22px var(--font-mono)', color: 'var(--fg-1)' }}>
              {member.email}
            </div>
          </div>
          <div className="d-section">
            <div className="d-label">Rolle</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {ROLES.map((r) => (
                <button
                  key={r}
                  type="button"
                  className={`chip${member.role === r ? ' on' : ''}`}
                  onClick={() => onChange({ role: r })}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
          <div className="d-section">
            <div className="d-label">Status</div>
            <button
              type="button"
              className={`switch${member.active ? ' on' : ''}`}
              onClick={() => onChange({ active: !member.active })}
              aria-label={member.active ? 'Deaktivieren' : 'Aktivieren'}
            />
          </div>
        </div>
        <footer className="drawer-foot">
          <button type="button" className="btn">
            <span className="material-symbols-rounded">refresh</span>
            Passwort-Reset senden
          </button>
          <button type="button" className="btn danger">
            <span className="material-symbols-rounded">delete</span>
            Entfernen
          </button>
          <button type="button" className="btn" style={{ marginLeft: 'auto' }} onClick={onClose}>
            Schließen
          </button>
        </footer>
      </aside>
    </>
  );
}

function InviteDrawer({ onClose }: { onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('Moderator');

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="scrim" onClick={onClose} aria-hidden="true" />
      <aside className="drawer" role="dialog" aria-label="User einladen">
        <header className="drawer-head">
          <h2>User einladen</h2>
          <button type="button" className="iconbtn" onClick={onClose} aria-label="Schließen">
            <span className="material-symbols-rounded">close</span>
          </button>
        </header>
        <div className="drawer-body">
          <div className="d-section">
            <div className="d-label">E-Mail</div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              placeholder="user@firma.com"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border-1)',
                borderRadius: 12,
                font: '500 14px/20px var(--font-body)',
                outline: 'none',
              }}
            />
          </div>
          <div className="d-section">
            <div className="d-label">Rolle</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {ROLES.map((r) => (
                <button
                  key={r}
                  type="button"
                  className={`chip${role === r ? ' on' : ''}`}
                  onClick={() => setRole(r)}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>
        <footer className="drawer-foot">
          <button
            type="button"
            className="btn primary"
            disabled={!email.trim()}
            onClick={() => {
              alert(`Einladung an ${email} mit Rolle ${role} verschickt (Mock).`);
              onClose();
            }}
          >
            <span className="material-symbols-rounded">send</span>
            Einladung senden
          </button>
          <button type="button" className="btn" style={{ marginLeft: 'auto' }} onClick={onClose}>
            Abbrechen
          </button>
        </footer>
      </aside>
    </>
  );
}
